import { useEffect, useRef } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useBranch } from '../contexts/BranchContext';
import GoftinoService from '../services/goftinoService';
import { logger } from '../utils/logger';

interface UseGoftinoOptions {
  enabled?: boolean;
}

/**
 * Hook to manage Goftino chat widget integration with user authentication
 * Automatically sets user information in Goftino when authenticated
 * and updates when branch changes
 */
export const useGoftino = (options?: UseGoftinoOptions) => {
  const { enabled = true } = options || {};
  const { user, isAuthenticated } = useAuth();
  const { selectedBranch } = useBranch();
  const hasSetInitialUser = useRef(false);

  // Handle Goftino ready event and set initial user info
  useEffect(() => {
    if (!enabled) {
      return;
    }

    const handleGoftinoReady = async () => {
      try {
        logger.info('Goftino ready, checking authentication...', 'useGoftino');

        // Skip if user is not authenticated
        if (!isAuthenticated || !user) {
          logger.info('User not authenticated, skipping Goftino setup', 'useGoftino', {
            isAuthenticated,
            hasUser: !!user
          });
          return;
        }

        // Set user information in Goftino
        const branchName = selectedBranch?.name || '';
        const mobile = user.mobile || '';
        
        logger.info('Preparing to set Goftino user', 'useGoftino', {
          userName: user.name,
          userEmail: user.email,
          mobile: mobile,
          branchName,
          hasMobile: !!mobile
        });
        
        GoftinoService.setUser({
          name: user.name || user.email.split('@')[0],
          about: branchName,
          phone: mobile,
          forceUpdate: true
        });

        hasSetInitialUser.current = true;
        logger.info('Goftino user info set on ready', 'useGoftino', {
          userName: user.name,
          branchName,
          mobile: mobile,
          hasPhone: !!mobile
        });
      } catch (error) {
        logger.error('Failed to set Goftino user on ready', 'useGoftino', error);
      }
    };

    // Listen for goftino_ready event
    window.addEventListener('goftino_ready', handleGoftinoReady);

    // If Goftino is already available, call handler immediately
    if (GoftinoService.isAvailable() && !hasSetInitialUser.current) {
      handleGoftinoReady();
    }

    return () => {
      window.removeEventListener('goftino_ready', handleGoftinoReady);
    };
  }, [enabled, isAuthenticated, user, selectedBranch]);

  // Monitor branch changes and update Goftino
  useEffect(() => {
    if (!enabled || !isAuthenticated || !user || !hasSetInitialUser.current) {
      return;
    }

    // Only update if Goftino is available
    if (!GoftinoService.isAvailable()) {
      return;
    }

    try {
      const branchName = selectedBranch?.name || '';
      const mobile = user.mobile || '';
      
      logger.info('Branch changed, updating Goftino...', 'useGoftino', {
        branchName,
        mobile,
        hasMobile: !!mobile
      });

      GoftinoService.setUser({
        name: user.name || user.email.split('@')[0],
        about: branchName,
        phone: mobile,
        forceUpdate: true
      });
    } catch (error) {
      logger.error('Failed to update Goftino on branch change', 'useGoftino', error);
    }
  }, [enabled, isAuthenticated, user, selectedBranch]);

  // Clear Goftino user info when user logs out
  useEffect(() => {
    if (!enabled) {
      return;
    }

    // If user was authenticated but now is not, clear Goftino
    if (!isAuthenticated && hasSetInitialUser.current) {
      logger.info('User logged out, clearing Goftino user info', 'useGoftino');
      GoftinoService.clearUser();
      hasSetInitialUser.current = false;
    }
  }, [enabled, isAuthenticated]);
};
